"use client";

import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
interface ScrollableFilterSectionProps {
	children: React.ReactNode;
	className?: string;
	hideScrollButtons?: boolean;
}

export default function ScrollableFilterSection({
	children,
	className = "",
	hideScrollButtons = false,
}: ScrollableFilterSectionProps) {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		containScroll: "trimSnaps",
		dragFree: true,
	});
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	const updateScrollButtons = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollLeft(emblaApi.canScrollPrev());
		setCanScrollRight(emblaApi.canScrollNext());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		updateScrollButtons();
		emblaApi.on("select", updateScrollButtons);
		emblaApi.on("reInit", updateScrollButtons);

		return () => {
			emblaApi.off("select", updateScrollButtons);
			emblaApi.off("reInit", updateScrollButtons);
		};
	}, [emblaApi, updateScrollButtons]);

	return (
		<div className="relative">
			{!hideScrollButtons && (
				<button
					onClick={scrollPrev}
					disabled={!canScrollLeft}
					className={`absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-md transition-opacity hover:bg-white md:block ${
						canScrollLeft ? "opacity-100" : "pointer-events-none opacity-0"
					}`}
					aria-label="Scroll left"
				>
					<ChevronLeft className="h-4 w-4 text-gray-600" />
				</button>
			)}

			{/* Scrollable content */}
			<div className="embla" ref={emblaRef}>
				<div
					className={`embla__container flex items-center justify-start gap-x-6 px-8 py-2 ${className}`}
				>
					{React.Children.map(children, (child, index) => (
						<div key={index} className="embla__slide flex-shrink-0">
							{child}
						</div>
					))}
				</div>
			</div>

			{!hideScrollButtons && (
				<button
					onClick={scrollNext}
					disabled={!canScrollRight}
					className={`absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-md transition-opacity hover:bg-white md:block ${
						canScrollRight ? "opacity-100" : "pointer-events-none opacity-0"
					}`}
					aria-label="Scroll right"
				>
					<ChevronRight className="h-4 w-4 text-gray-600" />
				</button>
			)}
			<style jsx>{`
				.embla {
					overflow: hidden;
				}
				.embla__container {
					display: flex;
					touch-action: pan-y pinch-zoom;
				}
				.embla__slide {
					flex: 0 0 auto;
				}
			`}</style>
		</div>
	);
}
